<script>
  import { createEventDispatcher } from "svelte";

  export let viewMode = "timeline";
  export let spans = [];
  export let traces = [];
  export let allRows = [];
  export let rangeLabel = "";
  export let colorBy = "name";
  export let rowSort = "tree";
  export let rowSearch = "";
  export let spanSearch = "";
  export let hiddenPids = new Set();

  const dispatch = createEventDispatcher();
</script>

<div
  class="shrink-0 border-b border-line-strong bg-surface-sunken px-3 py-2 flex flex-wrap gap-x-3 gap-y-1.5 items-center"
>
  <!-- View mode toggle -->
  <div class="flex rounded border border-line-strong overflow-hidden text-xs font-medium">
    <button
      class="px-3 py-1.5 {viewMode === 'timeline'
        ? 'bg-blue-600 text-white'
        : 'bg-surface text-content-muted hover:bg-surface-hover'}"
      on:click={() => (viewMode = "timeline")}
    >
      Timeline
    </button>
    <button
      class="px-3 py-1.5 border-l border-line-strong {viewMode === 'waterfall'
        ? 'bg-blue-600 text-white'
        : 'bg-surface text-content-muted hover:bg-surface-hover'}"
      on:click={() => (viewMode = "waterfall")}
    >
      Waterfall
    </button>
  </div>

  <!-- Unified stats -->
  <span class="text-xs text-content-subtle">
    <span class="font-medium text-content">{spans.length}</span> spans ·
    <span class="font-medium text-content">{traces.length}</span> traces ·
    <span class="font-medium text-content">{allRows.length}</span> processes
  </span>

  {#if viewMode === "timeline" && allRows.length > 0}
    <span class="text-gray-300 select-none">|</span>

    <!-- Zoom controls -->
    <div class="flex items-center gap-1">
      <button
        class="px-2 py-1 text-xs bg-surface border border-line-strong hover:bg-surface-hover text-content-muted rounded"
        on:click={() => dispatch("fitAll")}
        title="Fit all"
      >
        Fit
      </button>
      <button
        class="w-6 h-6 text-sm bg-surface border border-line-strong hover:bg-surface-hover text-content-muted rounded flex items-center justify-center leading-none"
        on:click={() => dispatch("zoomOut")}
        title="Zoom out"
      >
        −
      </button>
      <span class="text-xs text-content-subtle font-mono w-14 text-center tabular-nums">
        {rangeLabel}
      </span>
      <button
        class="w-6 h-6 text-sm bg-surface border border-line-strong hover:bg-surface-hover text-content-muted rounded flex items-center justify-center leading-none"
        on:click={() => dispatch("zoomIn")}
        title="Zoom in"
      >
        +
      </button>
    </div>

    <span class="text-gray-300 select-none">|</span>

    <!-- Color by -->
    <div class="flex items-center gap-1.5">
      <span class="text-xs text-content-subtle">Color</span>
      <div class="flex rounded border border-line-strong overflow-hidden text-xs">
        <button
          class="px-2 py-1 {colorBy === 'name'
            ? 'bg-blue-600 text-white'
            : 'bg-surface text-content-muted hover:bg-surface-hover'}"
          on:click={() => (colorBy = "name")}
        >
          Operation
        </button>
        <button
          class="px-2 py-1 border-l border-line-strong {colorBy === 'trace'
            ? 'bg-blue-600 text-white'
            : 'bg-surface text-content-muted hover:bg-surface-hover'}"
          on:click={() => (colorBy = "trace")}
        >
          Trace
        </button>
      </div>
    </div>

    <span class="text-gray-300 select-none">|</span>

    <!-- Sort -->
    <div class="flex items-center gap-1.5">
      <span class="text-xs text-content-subtle">Sort</span>
      <select
        bind:value={rowSort}
        class="text-xs bg-surface border border-line-strong text-content rounded px-2 py-1 pr-6"
      >
        <option value="tree">Process tree</option>
        <option value="time">First activity</option>
        <option value="name">Name</option>
        <option value="count">Span count</option>
      </select>
    </div>

    <!-- Process filter -->
    <input
      type="text"
      bind:value={rowSearch}
      placeholder="Filter processes…"
      class="text-xs bg-surface border border-line-strong text-content rounded px-2 py-1 w-32 placeholder-gray-400"
    />

    <!-- Span name filter -->
    <input
      type="text"
      bind:value={spanSearch}
      placeholder="Highlight spans…"
      class="text-xs bg-surface border border-line-strong text-content rounded px-2 py-1 w-32 placeholder-gray-400 {spanSearch
        ? 'border-yellow-400 bg-yellow-50'
        : ''}"
    />
  {/if}

  <!-- Hidden pids individual restore -->
  {#if hiddenPids.size > 0}
    <div class="flex items-center gap-1 flex-wrap">
      <span class="text-xs text-content-subtle">Hidden:</span>
      {#each [...hiddenPids] as pid}
        {@const row = allRows.find((r) => r.pid === pid)}
        <button
          class="text-xs bg-surface-muted border border-line-strong rounded px-1.5 py-0.5 hover:bg-red-50 hover:border-red-200 hover:text-red-700 transition-colors"
          on:click={() => dispatch("toggleHide", { pid })}
          title="Click to restore"
        >
          {row?.label || pid} ×
        </button>
      {/each}
    </div>
  {/if}
</div>
